/**
 * CTIAuditLog — Filterable audit event table (superadmin only). Shows every
 * user action with actor, role, and source IP for full activity traceability.
 */

import { useState, useEffect } from 'react';
import { DataTable, type Column } from '../../../components/DataTable';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import { getAuditEvents } from '../../../api/audit';
import type { AuditEvent } from '../../../api/types';

/**
 * ActivityTab — the user-activity audit table (filter + table + count).
 * Presentational tab body, embedded inside User Management's "Activity" tab.
 * No page header (the parent provides it).
 */
export function ActivityTab() {
  const { user } = useAuth();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState('');

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getAuditEvents(user.tenantId);
      setEvents(response.events);
    } catch {
      setError('Failed to load audit events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  if (loading) return <LoadingState message="Loading activity..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  if (events.length === 0) {
    return (
      <div className="glass-card text-center py-12">
        <p className="text-body text-slate-muted">
          No activity yet. User actions (with source IP) will appear here.
        </p>
      </div>
    );
  }

  const filteredEvents = filterAction
    ? events.filter((e) => e.action.toLowerCase().includes(filterAction.toLowerCase()))
    : events;

  const columns: Column<AuditEvent>[] = [
    {
      key: 'timestamp',
      header: 'Time',
      render: (row) => new Date(row.timestamp).toLocaleString(),
      width: '180px',
    },
    { key: 'action', header: 'Action' },
    { key: 'user_id', header: 'User', width: '170px' },
    { key: 'actor_role', header: 'Role', width: '100px', render: (row) => row.actor_role || '—' },
    { key: 'ip_address', header: 'IP Address', width: '130px', render: (row) => row.ip_address || '—' },
    { key: 'resource_id', header: 'Resource', render: (row) => row.resource_id || '—' },
    { key: 'details', header: 'Details' },
  ];

  return (
    <div className="space-y-6">
      <div className="glass-card">
        <label htmlFor="action-filter" className="block text-body font-semibold text-slate-text mb-2">
          Filter by Action
        </label>
        <input
          id="action-filter"
          type="text"
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          placeholder="e.g., USER_CREATED, LOGIN, REPORT"
          className="w-full sm:w-80 px-4 py-3 text-body rounded-btn border-2 focus:outline-none focus:border-primary-blue bg-white"
          style={{ borderColor: 'var(--color-card-border)' }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredEvents}
        keyExtractor={(row) => row.event_id}
        emptyMessage="No activity found."
      />

      <p className="text-[16px] text-slate-muted">
        Showing {filteredEvents.length} of {events.length} events
      </p>
    </div>
  );
}
